import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { postApi, categoryApi } from '../utils/api';
import { Post, Category } from '../types';
import { useAuthStore } from '../stores/authStore';
import { useLanguageStore } from '../stores/languageStore';
import toast from 'react-hot-toast';

const EditPost: React.FC = () => {
  const { language, t } = useLanguageStore();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();
  const [post, setPost] = React.useState<Post | null>(null);
  const [categories, setCategories] = React.useState<Category[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [title, setTitle] = React.useState('');
  const [content, setContent] = React.useState('');
  const [categoryId, setCategoryId] = React.useState<number>(0);

  React.useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const fetchData = async () => {
      if (!id) return;
      try {
        setLoading(true);
        // 获取帖子详情
        const postResponse = await postApi.getPostById(parseInt(id));
        if (postResponse.success && postResponse.data) {
          const postData = postResponse.data.post;
          setPost(postData);
          setTitle(postData.title);
          setContent(postData.content);
          setCategoryId(postData.category_id);

          // 检查权限
          if (postData.author_id !== user?.id && user?.role !== '管理员' && user?.role !== '站长') {
            toast.error(t('editPost.noPermission'));
            navigate(`/post/${id}`);
            return;
          }
        }

        // 获取版块列表
        const categoryResponse = await categoryApi.getCategories();
        if (categoryResponse.success && categoryResponse.data) {
          setCategories(categoryResponse.data);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
        toast.error(t('editPost.fetchFailed'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, navigate, isAuthenticated, user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !content.trim() || !categoryId) {
      toast.error(t('editPost.fillAll'));
      return;
    }

    try {
      setSubmitting(true);
      if (!id) return;
      const response = await postApi.updatePost(parseInt(id), {
        title,
        content,
        category_id: categoryId,
      });
      if (response.success) {
        toast.success(t('posts.updateSuccess'));
        navigate(`/post/${id}`);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('editPost.editFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">{t('editPost.notFound')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">{t('editPost.title')}</h1>
        <Link to={`/post/${id}`} className="btn btn-secondary">
          {t('editPost.backToPost')}
        </Link>
      </div>

      <div className="card p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
              {t('editPost.postTitle')}
            </label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder={t('editPost.enterTitle')}
              required
            />
          </div>

          <div>
            <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
              {t('editPost.category')}
            </label>
            <select
              id="category"
              value={categoryId}
              onChange={(e) => setCategoryId(parseInt(e.target.value))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            >
              <option value="">{t('editPost.selectCategory')}</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-1">
              {t('editPost.content')}
            </label>
            <textarea
              id="content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[300px]"
              placeholder={t('editPost.enterContent')}
              required
            />
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={submitting}
              className="btn btn-primary"
            >
              {submitting ? t('editPost.submitting') : t('editPost.saveChanges')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditPost;